import { apiClient } from '@/shared/services/client'
import type {
  ConfiguracionFidelizacion,
  ConfiguracionFidelizacionCreateInput,
  ConfiguracionFidelizacionListItem,
  ConfiguracionFidelizacionUpdateInput,
  ExportFormato,
  ExportResult,
  ExportTipo,
  FacturasIngresoResponse,
  FiltrosListadoBase,
  FiltrosRango,
  GrupoDisponible,
  MetadataResponse,
  MetricasAgrupacionResponse,
  MetricasCanjesResponse,
  MetricasComparativaResponse,
  MetricasPorProductoResponse,
  MetricasResumen,
  MetricasValidacionResponse,
  MetricasVentasPorTipoResponse,
  PaginatedListado,
  RecursoListado,
  SerieTemporalResponse,
  TestConnectionResult,
  WalletBalanceResponse,
} from '../types'

const CONFIG_URL = '/reportes-fidelizacion/config'
const PROXY_URL = '/reportes-fidelizacion'

function parseFilenameFromContentDisposition(header: string | undefined): string | null {
  if (!header) return null
  // filename*=UTF-8''nombre.xlsx (RFC 5987) tiene prioridad sobre filename="..."
  const utf8Match = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].trim())
    } catch {
      return utf8Match[1].trim()
    }
  }
  const match = header.match(/filename\s*=\s*"?([^";]+)"?/i)
  return match ? match[1].trim() : null
}

export const fidelizacionApi = {
  // ---- Configuraciones ----
  listConfiguraciones: async (params?: { grupo_id?: number; activo?: boolean }) => {
    const { data } = await apiClient.get<ConfiguracionFidelizacionListItem[]>(`${CONFIG_URL}/`, { params })
    return data
  },

  getConfiguracion: async (id: number) => {
    const { data } = await apiClient.get<ConfiguracionFidelizacion>(`${CONFIG_URL}/${id}`)
    return data
  },

  createConfiguracion: async (payload: ConfiguracionFidelizacionCreateInput) => {
    const { data } = await apiClient.post<ConfiguracionFidelizacion>(`${CONFIG_URL}/`, payload)
    return data
  },

  updateConfiguracion: async (id: number, payload: ConfiguracionFidelizacionUpdateInput) => {
    const { data } = await apiClient.put<ConfiguracionFidelizacion>(`${CONFIG_URL}/${id}`, payload)
    return data
  },

  deleteConfiguracion: async (id: number) => {
    const { data } = await apiClient.delete<{ message: string; success: boolean }>(`${CONFIG_URL}/${id}`)
    return data
  },

  testConnection: async (id: number) => {
    const { data } = await apiClient.post<TestConnectionResult>(`${CONFIG_URL}/${id}/test-connection`)
    return data
  },

  getGruposDisponibles: async () => {
    const { data } = await apiClient.get<GrupoDisponible[]>(`${CONFIG_URL}/grupos-disponibles`)
    return data
  },

  // ---- Proxy hacia el upstream ----
  ping: async (configId: number) => {
    const { data } = await apiClient.get<Record<string, unknown>>(`${PROXY_URL}/${configId}/ping`)
    return data
  },

  getMetadata: async (configId: number, forceRefresh = false) => {
    const { data } = await apiClient.get<MetadataResponse>(`${PROXY_URL}/${configId}/metadata`, {
      params: forceRefresh ? { force_refresh: true } : undefined,
    })
    return data
  },

  getMetricasResumen: async (
    configId: number,
    params?: FiltrosRango,
  ) => {
    const { data } = await apiClient.get<MetricasResumen>(`${PROXY_URL}/${configId}/metricas/resumen`, {
      params,
    })
    return data
  },

  getWalletBalance: async (configId: number) => {
    const { data } = await apiClient.get<WalletBalanceResponse>(`${PROXY_URL}/${configId}/wallet/balance`)
    return data
  },

  // ---- Listados (Fase 2) ----
  getListado: async (
    configId: number,
    recurso: RecursoListado,
    params?: FiltrosListadoBase,
  ) => {
    const { data } = await apiClient.get<PaginatedListado>(`${PROXY_URL}/${configId}/${recurso}`, { params })
    return data
  },

  getListadoItem: async (
    configId: number,
    recurso: Exclude<RecursoListado, 'movimientos-puntos'>,
    itemId: number | string,
  ) => {
    const { data } = await apiClient.get<Record<string, unknown>>(
      `${PROXY_URL}/${configId}/${recurso}/${itemId}`,
    )
    return data
  },

  // ---- Métricas (Fase 3) ----
  getMetricasPorProvincia: async (configId: number, params?: FiltrosRango & { top?: number }) => {
    const { data } = await apiClient.get<MetricasAgrupacionResponse>(
      `${PROXY_URL}/${configId}/metricas/por-provincia`,
      { params },
    )
    return data
  },

  getMetricasPorCampo: async (
    configId: number,
    campo: string,
    params?: Record<string, string | number | undefined>,
  ) => {
    const { data } = await apiClient.get<MetricasAgrupacionResponse>(
      `${PROXY_URL}/${configId}/metricas/por/${campo}`,
      { params },
    )
    return data
  },

  getMetricasPorProducto: async (configId: number, params?: FiltrosRango & { top?: number }) => {
    const { data } = await apiClient.get<MetricasPorProductoResponse>(
      `${PROXY_URL}/${configId}/metricas/por-producto`,
      { params },
    )
    return data
  },

  getMetricasVentasPorTipo: async (
    configId: number,
    params?: FiltrosRango & { top?: number },
  ) => {
    const { data } = await apiClient.get<MetricasVentasPorTipoResponse>(
      `${PROXY_URL}/${configId}/metricas/por-producto/ventas-por-tipo`,
      { params },
    )
    return data
  },

  getMetricasValidacion: async (configId: number, params?: FiltrosRango) => {
    const { data } = await apiClient.get<MetricasValidacionResponse>(
      `${PROXY_URL}/${configId}/metricas/validacion`,
      { params },
    )
    return data
  },

  getSerieTemporal: async (
    configId: number,
    params: Record<string, string | number | undefined>,
  ) => {
    const { data } = await apiClient.get<SerieTemporalResponse>(
      `${PROXY_URL}/${configId}/metricas/serie-temporal`,
      { params },
    )
    return data
  },

  getMetricasCanjes: async (configId: number, params?: FiltrosRango) => {
    const { data } = await apiClient.get<MetricasCanjesResponse>(
      `${PROXY_URL}/${configId}/metricas/canjes`,
      { params },
    )
    return data
  },

  getFacturasIngreso: async (configId: number, params?: FiltrosRango) => {
    const { data } = await apiClient.get<FacturasIngresoResponse>(
      `${PROXY_URL}/${configId}/metricas/facturas-ingreso`,
      { params },
    )
    return data
  },

  // ---- Exports (Fase 4) ----
  exportar: async (
    configId: number,
    tipo: ExportTipo,
    formato: ExportFormato,
    params?: Record<string, string | number | boolean | undefined>,
  ): Promise<ExportResult> => {
    const response = await apiClient.get<Blob>(
      `${PROXY_URL}/${configId}/export/${tipo}/${formato}`,
      { params, responseType: 'blob' },
    )
    const headers = response.headers as Record<string, string>
    const remainingHeader = headers['x-ratelimit-remaining']
    const remaining = remainingHeader !== undefined ? Number(remainingHeader) : null
    return {
      blob: response.data,
      filename: parseFilenameFromContentDisposition(headers['content-disposition'])
        ?? `${tipo}.${formato}`,
      contentType: headers['content-type'] ?? 'application/octet-stream',
      rateLimitRemaining: Number.isFinite(remaining) ? (remaining as number) : null,
    }
  },

  getMetricasComparativa: async (
    configId: number,
    params: {
      periodo_a_desde: string
      periodo_a_hasta: string
      periodo_b_desde: string
      periodo_b_hasta: string
      provincia?: string
    },
  ) => {
    const { data } = await apiClient.get<MetricasComparativaResponse>(
      `${PROXY_URL}/${configId}/metricas/comparativa`,
      { params },
    )
    return data
  },
}
